import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Download, Bell, Calendar, Coins, Globe, Building2, BookOpen } from "lucide-react";

export default function NotificationDetail({ item, open, onClose, onSaveAlert }) {
  if (!item) return null;

  const downloadPdf = async () => {
    const tid = toast.loading("Compiling briefing PDF… (up to 45s — an AI analyst is drafting sections)", { duration: 60000 });
    try {
      const res = await fetch(`${API}/funding/download-notification`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("gf_token")}` },
        body: JSON.stringify({
          name: item.name, agency: item.agency, geography: item.geography, discipline: item.discipline,
          deadline: item.deadline, amount: item.amount, url: item.url, summary: item.summary,
        }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(item.name || "notification").replace(/[^A-Za-z0-9 _-]/g,"_")}.pdf`;
      a.click();
      toast.dismiss(tid);
      toast.success("Briefing PDF downloaded — 10 sections tailored to this call");
    } catch { toast.dismiss(tid); toast.error("Could not download notification"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="overline">{item.agency}</div>
          <DialogTitle className="font-display text-3xl leading-tight">{item.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">{item.summary}</p>
          <div className="grid grid-cols-2 gap-3">
            <KV icon={Building2} label="Agency" value={item.agency}/>
            <KV icon={Globe} label="Geography" value={item.geography}/>
            <KV icon={Calendar} label="Deadline" value={item.deadline}/>
            <KV icon={Coins} label="Amount" value={item.amount}/>
            <KV icon={BookOpen} label="Discipline" value={item.discipline}/>
            {item.url && (
              <div className="col-span-2 flex items-center gap-2 text-sm">
                <ExternalLink className="w-4 h-4 text-primary"/>
                <a href={item.url} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all" data-testid="notif-detail-link">
                  {item.url}
                </a>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="notif-download-btn"><Download className="w-4 h-4 mr-1"/>Download PDF</Button>
          {item.url && (
            <a href={item.url} target="_blank" rel="noreferrer">
              <Button variant="outline"><ExternalLink className="w-4 h-4 mr-1"/>Visit page</Button>
            </a>
          )}
          <Button onClick={()=>{ onSaveAlert?.(item); }} data-testid="notif-save-alert-btn"><Bell className="w-4 h-4 mr-1"/>Save as alert</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KV({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-primary mt-0.5"/>
      <div>
        <div className="overline">{label}</div>
        <div className="mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );
}
